import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import BuilderContent from './BuilderContent';
import Navigation from './Navigation';
import BottomNavigation from './BottomNavigation';
import MobileNavigation from './MobileNavigation';
import { ResumeData } from '../../../types/resume';
import AdSpace from '../../ads/AdSpace';
import { generateSampleData } from '../../../utils/sampleData';
import LoadingScreen from '../../ui/LoadingScreen';
import { Wand2 } from 'lucide-react';
import { useResumeAnalytics } from '../../../hooks/useResumeAnalytics';
import { STEP_IDS, isFirstStep, isLastStep, getStepIndex } from './constants';
import type { StepId } from './constants';

export default function ResumeBuilder() {
  const [currentStep, setCurrentStep] = useState<StepId>('contact');
  const [isLoading, setIsLoading] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [resumeData, setResumeData] = useState<ResumeData>({
    template: 'minimal',
    personalInfo: {
      fullName: '',
      email: '',
      phone: '',
      location: '',
      title: '',
      summary: '',
      linkedin: '',
      website: ''
    }
  });

  const currentStepIndex = getStepIndex(currentStep);
  const isFirst = isFirstStep(currentStep);
  const isLast = isLastStep(currentStep);
  
  useResumeAnalytics(currentStep);

  const handleNext = () => {
    if (!isLast) {
      setCurrentStep(STEP_IDS[currentStepIndex + 1]);
    }
  };

  const handlePrevious = () => {
    if (!isFirst) {
      setCurrentStep(STEP_IDS[currentStepIndex - 1]);
    }
  };

  const handleUpdate = (data: Partial<ResumeData>) => {
    setHasStarted(true);
    setResumeData(prev => ({ ...prev, ...data }));
    
    if (!hasStarted) {
      window.alert('Privacy Notice: Your data is stored locally in your browser only and will be cleared when you close the tab. No personal information is saved on our servers.');
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <LoadingScreen show={isLoading} onComplete={() => setIsLoading(false)} />

      <div className={`min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50 ${isLoading ? 'hidden' : ''}`}>
        <div className="flex flex-col lg:flex-row min-h-screen">
          {/* Left Sidebar */}
          <div className="hidden lg:flex flex-col w-64 border-r border-gray-200">
            <Sidebar 
              currentStep={currentStep} 
              onStepChange={setCurrentStep}
              hasStarted={hasStarted}
            />
            <AdSpace location="resume_builder_left" className="mt-auto p-4" />
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Mobile Navigation */}
            <div className="lg:hidden">
              <MobileNavigation
                currentStep={currentStep}
                onNext={handleNext}
                onPrevious={handlePrevious}
                isFirstStep={isFirst}
                isLastStep={isLast}
              />
              {/* Mobile Top Ad */}
              <div className="px-4 py-2">
                <AdSpace location="resume_builder_mobile_top" className="h-16 w-full rounded-lg shadow-sm" />
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              <div className="max-w-3xl mx-auto">
                <BuilderContent 
                  currentStep={currentStep}
                  data={resumeData}
                  onUpdate={handleUpdate}
                />
              </div>
            </div>

            {/* Bottom Navigation & Actions */}
            <div className="border-t border-gray-200">
              {/* Mobile Ad Space */}
              <div className="lg:hidden px-4 py-2">
                <AdSpace location="resume_builder_mobile_bottom" className="h-16 w-full rounded-lg shadow-sm" />
              </div>

              {/* Desktop Navigation */}
              <div className="hidden lg:block">
                <Navigation
                  currentStep={currentStep}
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  isFirstStep={isFirst}
                  isLastStep={isLast}
                />
              </div>

              {/* Mobile Bottom Navigation */}
              <div className="lg:hidden">
                <BottomNavigation
                  currentStep={currentStep}
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  isFirstStep={isFirst}
                  isLastStep={isLast}
                />
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="hidden lg:flex flex-col w-64 border-l border-gray-200">
            <AdSpace location="resume_builder_right" className="h-full p-4" />
          </div>
        </div>
      </div>
    </>
  );
}